import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Save } from 'lucide-react'
import VoiceSectionPage from '@/components/layout/VoiceSectionPage'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/supabaseClient'

function getStorageKey(profileId) {
  return `voice-ramais-${profileId || 'anon'}`
}

export default function RamaisPage() {
  const { getProfileId } = useAuth()
  const profileId = getProfileId()

  const [loading, setLoading] = useState(true)
  const [attendants, setAttendants] = useState([])
  const [ramaisMap, setRamaisMap] = useState({})

  useEffect(() => {
    const key = getStorageKey(profileId)
    try {
      const raw = localStorage.getItem(key)
      if (raw) setRamaisMap(JSON.parse(raw))
    } catch {
      setRamaisMap({})
    }
  }, [profileId])

  useEffect(() => {
    async function loadAttendants() {
      if (!profileId) {
        setAttendants([])
        setLoading(false)
        return
      }

      setLoading(true)
      const { data, error } = await supabase
        .from('attendants')
        .select('id, name, email, is_active')
        .eq('profile_id', profileId)
        .order('name', { ascending: true })

      if (error) {
        console.error('[RamaisPage]', error)
        toast.error('Não foi possível carregar atendentes.')
        setAttendants([])
      } else {
        setAttendants(data || [])
      }
      setLoading(false)
    }

    loadAttendants()
  }, [profileId])

  const assignedCount = useMemo(
    () => Object.values(ramaisMap).filter((v) => String(v || '').trim()).length,
    [ramaisMap]
  )

  function updateRamal(attendantId, value) {
    const digits = value.replace(/\D/g, '').slice(0, 5)
    setRamaisMap((prev) => ({ ...prev, [attendantId]: digits }))
  }

  function saveRamais() {
    const key = getStorageKey(profileId)
    localStorage.setItem(key, JSON.stringify(ramaisMap))
    toast.success('Ramais salvos.')
  }

  return (
    <VoiceSectionPage
      title="Cadastrar ramais"
      description="Escolha o número interno de cada atendente. Base de atendentes reaproveitada do WappDesk."
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
          {assignedCount} ramal(is) configurado(s)
        </div>

        {loading ? (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Carregando atendentes...
          </div>
        ) : attendants.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Nenhum atendente encontrado nesta conta.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <ul className="divide-y divide-border">
              {attendants.map((att) => (
                <li key={att.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{att.name || att.email}</p>
                    <p className="truncate text-xs text-muted-foreground">{att.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${att.is_active ? 'bg-emerald-500/15 text-emerald-600' : 'bg-slate-500/15 text-slate-600'}`}>
                      {att.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                    <input
                      type="text"
                      value={ramaisMap[att.id] || ''}
                      onChange={(e) => updateRamal(att.id, e.target.value)}
                      placeholder="Ramal"
                      className="h-10 w-24 rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={saveRamais}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            <Save className="h-4 w-4" />
            Salvar ramais
          </button>
        </div>
      </div>
    </VoiceSectionPage>
  )
}
